/**
 * What every screen gets: who is signed in, the org's library, the venue's
 * state, and navigation. Screens read it with `useApp()`; App.tsx provides it.
 *
 * Keep this the only place that holds cross-screen state. A screen keeps its
 * own UI state (filters, selection, drafts) locally.
 */

import { createContext, useContext } from "react";
import type { Spec, SpecOverride } from "@baros/core";
import type { Library, Me, VenueState } from "./api.js";
import type { ProposedBuild } from "./Chat.js";

/** Where the app is. `params` are strings so a screen can be reached by name. */
export interface ScreenState {
  readonly kind: string;
  readonly params?: Readonly<Record<string, string | undefined>>;
}

export interface AppState {
  readonly token: string;
  readonly email: string;
  readonly me: Me;
  readonly library: Library;
  readonly venueState: VenueState | null;
  /** The venue this session is acting at — the first assigned one. */
  readonly venueId: string | null;
  readonly overrides: ReadonlyMap<string, SpecOverride>;
  readonly screen: ScreenState;
  /**
   * Move to a screen. Pushes a history entry, so back returns here, and
   * mounts the screen afresh. With `replace`, rewrites the address in place
   * and keeps the screen mounted: for a screen recording its own state, a
   * tab or a selection, in the URL without filling the back button with it.
   */
  readonly go: (screen: ScreenState, options?: { readonly replace?: boolean }) => void;
  /** Re-read the library from the API. */
  readonly refreshLibrary: () => Promise<void>;
  /** Re-read the venue state from the API. */
  readonly refreshVenue: () => Promise<void>;
  /** Put a saved spec into the library without a round trip. */
  readonly replaceSpec: (spec: Spec) => void;
  /** A build handed from the development chat to the editor. */
  readonly draftBuild: ProposedBuild | null;
  readonly setDraftBuild: (build: ProposedBuild | null) => void;
}

export const AppContext = createContext<AppState | null>(null);

export function useApp(): AppState {
  const state = useContext(AppContext);
  if (!state) throw new Error("useApp() outside the app");
  return state;
}
